import { sileo } from "sileo";
import i18n from "i18next";

import { openWorkspaceAddonCheckout } from "./workspaceAddon";

/**
 * El backend rechaza el broadcast cloud cuando se supera el cupo de workspaces
 * concurrentes con `{ error: "concurrent_limit_reached", limit, active }` (HTTP 400).
 * El error llega al cliente como `Error("HTTP 400: <json>")` (ver services/api.ts),
 * así que parseamos el JSON del message. Devuelve null si no es ese caso.
 */
const parseConcurrentLimit = (
  errorMsg: string,
): { limit?: number; active?: number } | null => {
  // Extrae el cuerpo JSON: "HTTP 400: {...}", un body JSON crudo, o "<prefijo> {...}".
  const httpMatch = errorMsg.match(/^HTTP \d+: (.*)$/s);
  const brace = errorMsg.indexOf("{");
  const jsonStr = httpMatch
    ? httpMatch[1]
    : brace >= 0
      ? errorMsg.slice(brace)
      : errorMsg;
  try {
    const body = JSON.parse(jsonStr) as {
      error?: string;
      limit?: number;
      active?: number;
    };
    if (body?.error === "concurrent_limit_reached") {
      return { limit: body.limit, active: body.active };
    }
  } catch {
    // body no-JSON → no es el caso de límite.
  }
  return null;
};

/**
 * Si `errorMsg` es el error de cupo de workspaces concurrentes cloud, muestra un toast
 * con CTA al checkout del add-on y devuelve true (manejado). Si no, devuelve false para
 * que el caller muestre su error genérico. Self-contained (usa la instancia global de
 * i18n) para poder llamarse desde cualquier pantalla sin importar su namespace.
 */
export async function maybeShowConcurrentLimitToast(
  errorMsg: string,
): Promise<boolean> {
  const limit = parseConcurrentLimit(errorMsg);
  if (!limit) return false;

  await i18n.loadNamespaces("streaming");
  const tt = (k: string, opts?: Record<string, unknown>): string =>
    i18n.t(`streaming:STREAMING_ERRORS.${k}`, opts);

  sileo.error({
    title: tt("concurrentLimitTitle"),
    description: tt("concurrentLimitBody", {
      active: limit.active ?? "",
      limit: limit.limit ?? "",
    }),
    duration: 10000,
    button: {
      title: tt("concurrentLimitCta"),
      onClick: () => {
        void openWorkspaceAddonCheckout();
      },
    },
  });

  return true;
}
